import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { DraasApiLastVMUpdateTime } from '@pure/paas-api-gateway-client-ts';
import { GenericService } from './generic.service';
import { DisasterRecoveryVirtualMachine } from '../models/disaster-recovery-virtual-machine';
import { ListParams } from '../interfaces/list-params';
import { isArray } from 'lodash';
import { DraasApiConfig } from './disaster-recovery-constants';
import { map, Observable, repeat, Subject, SubscriptionLike, switchMap } from 'rxjs';
import { DataPage } from '../interfaces/data-page';
import { DisasterRecoveryThrottlingHttpClient } from './disaster-recovery-throttling-http-client.service';
import { smartTimer } from '@pstg/smart-timer';

@Injectable({ providedIn: 'root' })
export class DisasterRecoveryVirtualMachinesService extends GenericService<DisasterRecoveryVirtualMachine> {
    private readonly LAST_VM_UPDATE_TIME_REFRESH_INTERVAL_MS = 60000;

    // Subscription for fetching last VM update time in periodic interval (smartTimer based)
    private lastVMUpdateTimeFetcherSubscription: SubscriptionLike;
    // Observable for emitting new value for last VM update time
    private lastVMUpdateTime$ = new Subject<Date | null>();
    // Keeping track of active subscriptions on lastVMUpdateTime$
    private lastVmUpdateTimeSubscriptions: Map<string, SubscriptionLike> = new Map();
    // Observable for manually triggering last VM update time fetch
    refreshGetLastVMUpdateTime$ = new Subject<void>();

    constructor(
        protected http: HttpClient,
        private throttlingHttpClient: DisasterRecoveryThrottlingHttpClient,
    ) {
        super({
            resourceClass: DisasterRecoveryVirtualMachine,
            endpoint: `${DraasApiConfig.getUrlPrefix()}/api/2.0/clusters/{clusterId}/vms`,
            list: true,
            create: false,
            update: false,
            delete: false,
        });
    }

    private getEndpoint(clusterId: string): string {
        return `${DraasApiConfig.getUrlPrefix()}/api/2.0/clusters/${clusterId}/vms`;
    }

    override list(
        params?: ListParams<DisasterRecoveryVirtualMachine>,
    ): Observable<DataPage<DisasterRecoveryVirtualMachine>> {
        return this.throttlingHttpClient.queueRequest(super.list(params));
    }

    override getListRequest(params: ListParams<DisasterRecoveryVirtualMachine>): { url: string } {
        const queryParams: string[] = [];
        const clusterId: string = params.filter.clusterId as string;

        queryParams.push(`page_number=${Math.floor(params.pageStart / params.pageSize)}`);
        queryParams.push(`page_size=${params.pageSize}`);

        if (params.filter || params.defaultFilter) {
            const filter = { ...params.filter, ...params.defaultFilter };
            Object.keys(filter)
                .filter(k => k != null && k !== 'clusterId')
                .forEach(k => queryParams.push(`${k}=${encodeURIComponent(filter[k] as string)}`));
        }

        if (params.sort) {
            if (isArray(params.sort)) {
                params.sort.forEach(s => queryParams.push(`sort=${s.key},${s.order}`));
            } else {
                queryParams.push(`sort=${params.sort.key},${params.sort.order}`);
            }
        }
        if (!queryParams.find(p => p.indexOf('sort=name') >= 0)) {
            queryParams.push(`sort=name,ASC`); // Append name sort if not already contained to stabilize sorts by host and datacenter
        }

        const url = this.getEndpoint(clusterId) + (queryParams.length > 0 ? `?${queryParams.join('&')}` : '');
        return { url };
    }

    getLastVMUpdateTime(clusterId: string): Observable<Date | null> {
        const url = `${this.getEndpoint(clusterId)}/last-vm-update-time`;
        return this.throttlingHttpClient
            .get<DraasApiLastVMUpdateTime>(url)
            .pipe(map(response => (response.last_update_time ? new Date(response.last_update_time) : null)));
    }

    /**
     * Creates a subscriber to keep track of last VM update time.
     * If you need to have multiple subscribers, you need to call this function with different keys.
     */
    subscribeToLastVMUpdateTime(key: string, clusterId: string, callback: (lastUpdateTime: Date | null) => void): void {
        if (this.lastVmUpdateTimeSubscriptions.has(key)) {
            // If we have already registered subscriber for given key, unsubscribe from it
            this.lastVmUpdateTimeSubscriptions.get(key).unsubscribe();
        }
        // Create new subscriber with callback for given key
        this.lastVmUpdateTimeSubscriptions.set(key, this.lastVMUpdateTime$.subscribe(callback));

        if (!this.lastVMUpdateTimeFetcherSubscription || this.lastVMUpdateTimeFetcherSubscription.closed) {
            // We need to start fetching last VM update time
            this.lastVMUpdateTimeFetcherSubscription = smartTimer(0, this.LAST_VM_UPDATE_TIME_REFRESH_INTERVAL_MS)
                .pipe(
                    switchMap(() =>
                        this.getLastVMUpdateTime(clusterId).pipe(
                            // Fetching new value on demand
                            repeat({ delay: () => this.refreshGetLastVMUpdateTime$ }),
                        ),
                    ),
                )
                // Push new value to lastVMUpdateTime$ observable for subscribers to receive
                .subscribe(lastUpdateTime => this.lastVMUpdateTime$.next(lastUpdateTime));
        } else {
            // Not our first subscriber,
            this.refreshGetLastVMUpdateTime$.next();
        }
    }

    /**
     * Unsubscribes from last VM update time observable for given key.
     */
    unsubscribeFromLastVMUpdateTime(key: string): void {
        if (this.lastVmUpdateTimeSubscriptions.has(key)) {
            const subscription = this.lastVmUpdateTimeSubscriptions.get(key);
            if (!subscription.closed) {
                // Unsubscribe...
                subscription.unsubscribe();
            }
            // ... and remove from active subscriptions
            this.lastVmUpdateTimeSubscriptions.delete(key);
        }

        // When no active subscriptions left, we can unsubscribe from the fetcher
        if (
            this.lastVmUpdateTimeSubscriptions.size === 0 &&
            this.lastVMUpdateTimeFetcherSubscription &&
            !this.lastVMUpdateTimeFetcherSubscription.closed
        ) {
            this.lastVMUpdateTimeFetcherSubscription.unsubscribe();
        }
    }
}
